import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listPrograms, listTemplates, updateProgram, type Program, type TemplateDetail } from "@/api/client";
import { useSession } from "@/auth/session";
import { ErrorBanner, ToolbarButton } from "@/components/ui";

/** Programs choose their template (FR-14); a new batch takes that template's current version. */
export function TemplatePrograms({ template }: { template: TemplateDetail }) {
  const { can } = useSession();
  const programs = useQuery({ queryKey: ["programs"], queryFn: listPrograms });
  const using = (programs.data ?? []).filter((p) => p.template_id === template.id);

  return (
    <section aria-label="Programs" className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div>
        <h2 className="text-sm font-semibold">Programs</h2>
        <p className="mt-1 text-xs text-zinc-500">New batches for these programs issue with this template's current version.</p>
      </div>
      <ErrorBanner error={programs.error} />
      {template.current_version_id === null ? (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This template has no published version, so programs using it can't start batches until one is published.
        </p>
      ) : null}
      {programs.isSuccess && using.length === 0 ? (
        <p className="text-sm text-zinc-500">No program uses this template yet.</p>
      ) : (
        <ul className="divide-y divide-zinc-100 text-sm">
          {using.map((program) => (
            <li key={program.id} className="flex justify-between gap-3 py-2">
              <span className="font-medium text-zinc-900">{program.name}</span>
              <span className="text-zinc-500">{program.program_type}</span>
            </li>
          ))}
        </ul>
      )}
      {can("program.manage") ? <AssignProgram template={template} programs={programs.data ?? []} /> : null}
    </section>
  );
}

function AssignProgram({ template, programs }: { template: TemplateDetail; programs: Program[] }) {
  const queryClient = useQueryClient();
  const [programId, setProgramId] = useState("");
  const templates = useQuery({ queryKey: ["templates"], queryFn: listTemplates });
  const names = new Map((templates.data ?? []).map((t) => [t.id, t.name]));
  const others = programs.filter((p) => p.template_id !== template.id);

  const assign = useMutation({
    mutationFn: (program: Program) => updateProgram(program.id, { template_id: template.id }),
    onSuccess: async () => {
      setProgramId("");
      await queryClient.invalidateQueries({ queryKey: ["programs"] });
    },
  });
  const chosen = others.find((p) => p.id === programId);

  const current = (program: Program) =>
    program.template_id ? `uses ${names.get(program.template_id) ?? "another template"}` : "no template";

  return (
    <div className="space-y-2 border-t border-zinc-100 pt-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">Program</span>
          <select
            value={programId}
            onChange={(e) => {
              setProgramId(e.target.value);
              assign.reset();
            }}
            className="mt-1 block min-w-72 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          >
            <option value="">Choose a program</option>
            {others.map((program) => (
              <option key={program.id} value={program.id}>
                {program.name} ({current(program)})
              </option>
            ))}
          </select>
        </label>
        <ToolbarButton disabled={!chosen || assign.isPending} onClick={() => chosen && assign.mutate(chosen)}>
          Use this template
        </ToolbarButton>
      </div>
      {assign.isSuccess ? (
        <p role="status" className="text-sm font-medium text-emerald-700">
          {assign.variables.name} now issues with this template.
        </p>
      ) : null}
      <ErrorBanner error={assign.error} />
    </div>
  );
}
